import { CompanySpecificService, Contract, ContractCompanySpecificService, ContractLight } from "@/types";
import { CreateOrderFromContractValues } from "@/components/organisms/CreateOrderFromContract/index";

export const createOrderFromContractPlaceholder = (
  companyId?: number,
  userId?: number,
  contract?: ContractLight
): Partial<CreateOrderFromContractValues> | null => {
  if (!companyId || !userId || !contract) return null;
  return {
    companyId: companyId,
    userId: userId,
    amount: 0,
    unit: contract.contractProduct?.priceUnit.id ?? 1,
    orderAddress: null,
    contractId: contract.id,
    boxtypeId: 1,
    orderCurrency: contract.contractProduct?.currencyType,
    orderNeedsApproval: !contract.automaticSalesApproval,
    servicePriceOverrides: {},
    estimatedDelivery: new Date(),
    created: new Date(),
    batchNumber: "",
    latitude: "",
    longitude: "",
    capturedAt: null,
  };
};

export const getMappedServicesWithContractPricing = (
  companySpecificServicesData: CompanySpecificService[] | undefined,
  fullContract: Contract
): ContractCompanySpecificService[] => {
  if (!companySpecificServicesData || !fullContract?.companySpecificServices) return [];

  return companySpecificServicesData
    .filter(
      (service) =>
        service.priceUnit.code === "PPO" &&
        fullContract.companySpecificServices.some((s) => s.companySpecificServiceId === service.id)
    )
    .map((service) => {
      const contractService = fullContract.companySpecificServices.find(
        (s) => s.companySpecificServiceId === service.id
      );

      return {
        companySpecificServiceId: service.id,
        price: contractService?.price || service.price,
        currencyType: service.currency,
      };
    });
};

/** Swaps in the prices the user edited in the wizard, keyed by companySpecificServiceId. */
export const applyServicePriceOverrides = (
  services: ContractCompanySpecificService[],
  overrides: Record<number, number>
): ContractCompanySpecificService[] =>
  services.map((service) =>
    service.companySpecificServiceId in overrides
      ? { ...service, price: overrides[service.companySpecificServiceId] }
      : service
  );

export const determineIfEstimatedDeliveryIsRequired = (orderPlanning: boolean, automaticProccessing: boolean) => {
  return orderPlanning || automaticProccessing;
};

export type OrderCreationErrorReason =
  | "slotSelectionRequired"
  | "estimatedDeliveryInvalid"
  | "slotUnavailable"
  | "slotFull"
  | "slotFractionMismatch"
  | "generic";

/**
 * Maps a slot-reservation/order-creation failure to an actionable UI reason, per the EOP
 * handoff's status table (§12). 400 covers two distinct backend rejections that share a status
 * code — they're disambiguated using whether a slot was submitted, since the backend never
 * exposes a distinguishing error code/message for this case.
 */
export const mapOrderCreationError = (
  status: number | undefined,
  receptionSlotId: number | null
): OrderCreationErrorReason => {
  switch (status) {
    case 400:
      return receptionSlotId ? "estimatedDeliveryInvalid" : "slotSelectionRequired";
    case 404:
      return "slotUnavailable";
    case 409:
      return "slotFull";
    case 422:
      return "slotFractionMismatch";
    default:
      return "generic";
  }
};
